import React from 'react';
import { useTranslation } from 'react-i18next';

import { useField } from 'formik';

import { appReactMemo } from 'hocs';

import Input from '../../Controls/Input';
import { IInputProps } from '../../Controls/Input/styled';

export interface IFormikInput extends IInputProps {
  externalError?: string;
  ignoreTouched?: boolean;
  onFieldChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  valueValidator?: (value: string, e: React.ChangeEvent<HTMLInputElement>) => boolean;
}

const FormikInput = ({
  externalError,
  ignoreTouched = false,
  isAnimatedLabel = false,
  name = '',
  onChange,
  onFieldChange,
  shouldValidate = false,
  showError = true,
  sizeVariant = 'sm',
  valueValidator,
  ...rest
}: IFormikInput) => {
  const { t } = useTranslation();
  const [field, meta, helpers] = useField(name);

  const onChangeHandler = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      if (onFieldChange) {
        return onFieldChange?.(e);
      }

      const isValid = valueValidator?.(value, e) ?? true;
      if (isValid) {
        helpers.setValue(value, false);
        helpers.setError(undefined);
      }
      onChange?.(e);
    },
    [valueValidator, onFieldChange, onChange, helpers],
  );

  const onBlur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      helpers.setTouched(true, shouldValidate);
      rest.onBlur?.(e);
    },
    [rest.onBlur, helpers],
  );

  return (
    <Input
      {...rest}
      {...field}
      error={!!(meta.error && (ignoreTouched || meta.touched)) || !!externalError}
      errorMessage={t(meta.error as never) || externalError}
      isAnimatedLabel={isAnimatedLabel}
      onBlur={onBlur}
      onChange={shouldValidate ? field.onChange : onChangeHandler}
      showError={showError}
      sizeVariant={sizeVariant}
      value={field.value ?? ''}
    />
  );
};

export default appReactMemo(FormikInput);
